import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

interface NavItem {
  label: string;
  to: string;
  match: string;
}

const NAV_ITEMS: NavItem[] = [
  { label: "首页", to: "/", match: "/" },
  { label: "电影", to: "/category/movie", match: "/category/movie" },
  { label: "电视剧", to: "/category/tv", match: "/category/tv" },
  { label: "综艺", to: "/category/variety", match: "/category/variety" },
  { label: "关于", to: "/about", match: "/about" },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleSearch = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      const trimmed = keyword.trim();
      if (!trimmed) {
        navigate("/search");
        return;
      }
      navigate(`/search?keyword=${encodeURIComponent(trimmed)}`);
    },
    [keyword, navigate],
  );

  const isActive = (item: NavItem) => {
    if (item.match === "/") return location.pathname === "/";
    return location.pathname.startsWith(item.match);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled ? "bg-background-50/95 backdrop-blur border-b border-background-200/80" : "bg-background-50 border-b border-transparent"
      }`}
    >
      <div className="flex h-16 items-center gap-4 px-4 md:px-6 lg:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-play-circle-fill text-xl"></i>
          </span>
          <span className="font-heading text-lg font-bold tracking-tight text-foreground-950">
            密桃<span className="text-primary-500">视频</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 ml-4" aria-label="主导航">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`rounded-md px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                isActive(item)
                  ? "text-primary-600 bg-primary-50"
                  : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <form onSubmit={handleSearch} className="ml-auto hidden md:flex items-center" role="search">
          <div className="relative flex items-center">
            <i className="ri-search-line absolute left-3 text-base text-foreground-400 pointer-events-none"></i>
            <input
              type="search"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索视频标题或主演"
              aria-label="搜索视频"
              className="h-10 w-52 lg:w-64 rounded-md border border-background-200/80 bg-background-100/70 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition-all focus:w-60 lg:focus:w-72 focus:border-primary-400 focus:bg-background-50"
            />
          </div>
          <button
            type="submit"
            className="ml-2 h-10 rounded-md bg-primary-500 px-4 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
          >
            搜索
          </button>
        </form>

        <button
          type="button"
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-label="打开导航菜单"
          aria-expanded={menuOpen}
          className="ml-auto flex h-10 w-10 items-center justify-center rounded-md border border-background-200/80 text-foreground-800 md:hidden"
        >
          <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200/80 bg-background-50 px-4 pb-5 pt-3">
          <form onSubmit={handleSearch} className="mb-3 flex items-center" role="search">
            <div className="relative flex flex-1 items-center">
              <i className="ri-search-line absolute left-3 text-base text-foreground-400 pointer-events-none"></i>
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索视频标题或主演"
                aria-label="搜索视频"
                className="h-10 w-full rounded-md border border-background-200/80 bg-background-100/70 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:bg-background-50"
              />
            </div>
            <button
              type="submit"
              className="ml-2 h-10 rounded-md bg-primary-500 px-4 text-sm font-medium text-background-50 whitespace-nowrap"
            >
              搜索
            </button>
          </form>
          <nav className="flex flex-col gap-1" aria-label="移动导航">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive(item)
                    ? "bg-primary-50 text-primary-600"
                    : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}